<template>
  <header class="header" :class="[
    { header_dark: isDark },
    { header_overlay: isOverlay },
    { header_hidden: isHidden }
  ]">
    <div class="max-width">
      <div class="header__grid">
        <router-link to="/" class="header__logo">
          <img src="@/assets/imgs/logo.svg" alt="Creative Commerce" />
          Creative Commerce
        </router-link>

        <nav class="header__menu">
          <router-link to="/themes">Themes</router-link>
          <router-link to="/contacts">Contacts</router-link>
          <router-link to="/help-center">Help Center</router-link>
        </nav>

        <div class="header__burger">
          <i></i><i></i><i></i>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import useBurgerMenu from '@/composables/useBurgerMenu.js'

const route = useRoute()

const isDark = computed(() => {
  return route.path === '/' || (route.path.startsWith('/themes/') && route.path !== '/themes/')
})

const isOverlay = ref(false)
const isHidden = ref(false)

onMounted(() => {
  const { initBurger, closeBurger } = useBurgerMenu()
  initBurger()

  let lastScrollTop = 0

  window.addEventListener('scroll', () => {
    const y = window.scrollY

    if (y > 0) closeBurger()
    isOverlay.value = y > 2

    if (y > lastScrollTop && y > 30) isHidden.value = true
    else isHidden.value = false

    lastScrollTop = y
  })
})
</script>

<style lang="scss">
.header {
  position: fixed;
  border-bottom: 1px solid transparent;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 99;
  transition: var(--transition);

  &_overlay {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.05);
  }

  &_hidden {
    transform: translateY(-100%);
  }

  &__grid {
    display: flex;
    align-items: center;
    height: 4.625rem;
    justify-content: space-between;
  }

  &__logo {
    position: relative;
    z-index: 10;
    display:flex;
    align-items:center;
    gap:6px;
    font-family:var(--font-secondary);
    font-weight: 700;
    font-size: 1.125rem;
    letter-spacing: -0.03em;
    color:var(--color-main);
  }

  &_dark:not(&_overlay):not(:has(.header__menu_show)) &__logo {
    color:#fff;
  }

  &__menu {
    display: flex;
    align-items: center;
    gap: 1.5rem;

    @include xs {
      position: fixed;
      top: 0;
      right: 0;
      transform: translateY(-100%);
      z-index: 5;
      background: #fff;
      border-bottom: 1px solid var(--color-border);
      flex-direction: column;
      transition: var(--transition);
      font-size: 1.125rem;
      width: 100%;
      padding: 5rem 0 3rem;

      &_show {
        transform: none;
      }
    }

    a {
      display: block;
      color: var(--color-main);

      @include xs {
        opacity: 0;
        transform: translateY(-5px);

        &:nth-child(1) {
          transition-delay: .4s;
        }
        &:nth-child(2) {
          transition-delay: .3s;
        }
        &:nth-child(3) {
          transition-delay: .2s;
        }
      }

      &:hover, &.router-link-active {
        opacity:.7;
      }
    }
  }

  &_dark:not(&_overlay):not(:has(.header__menu_show)) &__menu {
    a {
      color:#fff;
    }
  }

  @include xs {
    &:has(&__menu_show) &__menu {
      a {
        transform: none;
        opacity: 1;
      }
    }
  }

  &__burger {
    display: none;
    position: relative;
    z-index: 10;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 26px;

    @include xs {
      display: flex;
    }

    i {
      background: var(--color-main);
      width: 26px;
      display: block;
      height: 2px;
      margin: 3px 0;
      transition: width 0.1s linear;

      &:first-child {
        top: 0;
        transform: rotate(0deg);
        position: relative;
        transition: transform 0.2s linear, top 0.2s linear;
      }

      &:nth-child(2) {
        opacity: 1;
        transition: opacity 0.2s linear;
      }

      &:nth-child(3) {
        position: relative;
        top: 0;
        transform: rotate(0deg);
        transition: transform 0.2s linear, top 0.2s linear;
      }
    }

    &_active {
      i {
        background: var(--color-accent);

        &:first-child {
          top: 8px;
          transform: rotate(45deg);
          transition: transform 0.2s linear, top 0.2s linear;
        }

        &:nth-child(2) {
          opacity: 0;
          transition: opacity 0.2s linear;
        }

        &:nth-child(3) {
          top: -8px;
          transform: rotate(-45deg);
          transition: transform 0.2s linear, top 0.2s linear;
        }
      }
    }
  }

  &_dark:not(&_overlay):not(:has(.header__menu_show)) &__burger {
    i {
      background:#fff;
    }
  }
}
</style>
